import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { isTopDialog, popDialog, pushDialog, trapTabWithin } from '../utils/focus';

const WIDTHS = {
  sm: 440,
  md: 480,
  lg: 760,
  xl: 1040,
};

export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  icon: Icon,
  children,
  footer,
  footerJustify = 'flex-end',
  size = 'md',
  closeLabel = 'Close',
  closeOnBackdrop = true,
  className = '',
  contentStyle,
  layout = 'modal',
  headerActions,
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  // Keep the latest onClose in a ref so the open/close effect doesn't
  // tear down (and prematurely restore focus) when callers pass a new
  // inline closure on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    // Held in a local rather than read off the ref in the cleanup: React has
    // already detached the ref by the time a closing Modal's cleanup runs, so
    // popping dialogRef.current would pop nothing.
    const dialogNode = dialogRef.current;
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        // Only the topmost dialog closes: the command palette can be opened
        // over an open Modal, and both listen on document (doc-262). Tab is
        // deliberately NOT gated — trapTabWithin already pulls focus back in,
        // and the dialog above us traps it first anyway.
        if (isTopDialog(dialogNode)) onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      trapTabWithin(event, dialogRef.current);
    };

    // Registered alongside the listener so the two lifetimes cannot diverge.
    pushDialog(dialogNode);
    document.addEventListener('keydown', handleKeydown);
    document.body.style.overflow = 'hidden';
    if (dialogRef.current && !dialogRef.current.contains(document.activeElement)) {
      dialogRef.current.focus();
    }

    return () => {
      popDialog(dialogNode);
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: layout === 'drawer' ? 'flex-end' : 'center',
        padding: layout === 'drawer' ? 0 : 16,
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        onClick={closeOnBackdrop ? onClose : undefined}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--surface-backdrop)',
          backdropFilter: layout === 'drawer' ? undefined : 'blur(4px)',
        }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={`card ${layout === 'drawer' ? 'animate-slide-in motion-reduce:animate-none' : 'animate-pop'} ${className}`.trim()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: WIDTHS[size] || WIDTHS.md,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.35)',
          ...(layout === 'drawer' ? {
            width: 'min(100vw, max(80vw, 720px))',
            maxWidth: '100vw',
            height: '100dvh',
            maxHeight: '100dvh',
            borderRadius: 0,
          } : {}),
          ...contentStyle,
        }}
      >
        <div className="card-header">
          <div
            className="row"
            style={{
              gap: 8,
              minWidth: 0,
              flex: 1,
              alignItems: description ? 'flex-start' : 'center',
              flexDirection: description ? 'column' : 'row',
            }}
          >
            <div className="row" style={{ gap: 8, minWidth: 0 }}>
              {Icon && <Icon className="w-4 h-4" style={{ color: 'var(--text-purple)' }} />}
              <span id={titleId} className="card-title">{title}</span>
            </div>
            {description && (
              <div className="muted" style={{ fontSize: 12 }}>
                {description}
              </div>
            )}
          </div>
          {headerActions}
          <button onClick={onClose} className="icon-btn sm" title={closeLabel} aria-label={closeLabel}>
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
        {footer && (
          <div className="card-footer" style={{ justifyContent: footerJustify }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
